import {
  FlightSearchPayload,
  ServiceExtras,
  ServicePayload,
  ViewInfoExtra,
} from '../models/flight-payload';
import { getCommodityCodes } from '../models/product-mapping';
import { LoadItem } from '../models/view-flight-info-model';
import { buildPayload } from './payload-factory';

type Mapper<S extends keyof ServiceExtras> = (
  base: FlightSearchPayload
) => FlightSearchPayload & ServiceExtras[S];

export const serviceMappers: {
  [K in keyof ServiceExtras]: (base: FlightSearchPayload) => ServicePayload<K>;
} = {
  viewInfo: (base: FlightSearchPayload): FlightSearchPayload & ViewInfoExtra => {
    const carrierCode = base.flightNumbers?.[0] ?? '';
    const operationalNumber = base.flightNumbers?.[1] ?? '';

    return {
      ...base,
      ViewFlightSummaryRequest: {
        CommodityCode: getCommodityCodes(base.product ?? ''),

        DatedFlightLeg: {
          OperationalFlightNumber: operationalNumber,
          OperatingCarrierCode: carrierCode,
          OriginStation: base.departureStation,

          ScheduledDepartureDateLocal: base.flightDate,
        },

        FMLoadIndicator: true,
      },
    };
  },

  deadload: (base) => ({ ...base, DeadloadRequest: { weight: 1500, description: 'Cargo' } }),
  viewOwnership: (base) => ({
    ...base,
    OwnershipRequest: { ownerId: '123', ownerName: 'OpsTeam' },
  }),
  forecast: (base) => ({ ...base, ForecastRequest: { forecastType: 'Load', horizonDays: 7 } }),
  capacity: (base) => ({
    ...base,
    CapacityRequest: { includeContainers: true, includePallets: true },
  }),
  comments: (base) => ({
    ...base,
    CommentsRequest: { includeCrewComments: true, includeOpsComments: true },
  }),
  loadPlan: (base) => ({ ...base, LoadPlanRequest: { planVersion: 'v1', includeULD: true } }),
  fitment: (base) => ({ ...base, FitmentRequest: { fitmentType: 'Current', includeNG: true } }),
  passenger: (base) => ({
    ...base,
    PassengerRequest: { includeCabinDetails: true, includeMealCodes: false },
  }),
  product: (base) => ({
    ...base,
    ProductRequest: { includeOwnerDetails: true, includeManifestTimes: true },
  }),
  flightStatus: (base) => ({
    ...base,
    FlightStatusRequest: { includeScheduleStatus: true, includeLoadStatus: true },
  }),
  aircraft: (base) => ({
    ...base,
    AircraftRequest: { includeRegistration: true, includeSubtype: true },
  }),
  crew: (base) => ({ ...base, CrewRequest: { includeCrewList: true, includeCrewRoles: true } }),
  delay: (base) => ({ ...base, DelayRequest: { includeDelayCodes: true, includeReason: true } }),
};

export function buildViewFlightSummaryRequest(
  base: FlightSearchPayload,
  product: string,
  fmLoadIndicator: boolean = true
) {
  return {
    ViewFlightSummaryRequest: {
      CommodityCode: getCommodityCodes(product ?? ''),
      DatedFlightLeg: {
        OperationalFlightNumber: base.flightNumbers?.[1] ?? '',
        OperatingCarrierCode: base.flightNumbers?.[0] ?? '',
        OriginStation: base.departureStation,
        ScheduledDepartureDateLocal: formatDate(base.flightDate),
      },
      FMLoadIndicator: fmLoadIndicator,
    },
  };
}

function normalizeDepDateTime(dateInput: any): string {
  if (!dateInput) return '';

  const isoMatch = /^(\d{4}-\d{2}-\d{2})/.exec(String(dateInput));
  if (isoMatch) {
    return `${isoMatch[1]}T00:00:00`;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    return `${dateInput}T00:00:00`;
  }

  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return '';

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}T00:00:00`;
}
export function splitFlightNumber(full: string): { num: string; sfx: string } {
  if (!full) return { num: '', sfx: '' };

  const match = /^(\d+)([A-Za-z]?)$/.exec(full);

  return {
    num: match?.[1] ?? full,
    sfx: match?.[2] ?? '',
  };
}

export function buildSummaryPathFromViewInfo(base: FlightSearchPayload) {
  const mapped = buildPayload('viewInfo', base) as FlightSearchPayload & ViewInfoExtra;

  const req = mapped.ViewFlightSummaryRequest;
  const leg = req?.DatedFlightLeg ?? {};

  const carrierCode = leg.OperatingCarrierCode ?? '';
  const rawFlightNumber = leg.OperationalFlightNumber ?? '';
  const origin = leg.OriginStation ?? '';

  const { num: flightNo, sfx: flightNoSfx } = splitFlightNumber(rawFlightNumber);

  const depDateTime = normalizeDepDateTime(leg.ScheduledDepartureDateLocal);

  const commodityCodeCSV = Array.isArray(req?.CommodityCode)
    ? req!.CommodityCode.join(',')
    : req?.CommodityCode ?? '';

  const urlPath = `/cfs/retriveFlightSummary/${encodeURIComponent(
    carrierCode
  )}/${encodeURIComponent(flightNo)}`;

  const query: any = {
    commodityCode: commodityCodeCSV,
    origin,
    depDateTime,
    fmLoadIndicator: false,
  };

  if (flightNoSfx) {
    query.flightNoSfx = flightNoSfx;
  }

  return { urlPath, query };
}

function formatDateToDDMMMYYYY(isoDate?: string | null): string {
  if (!isoDate) return '';

  const [yyyy, mm, dd] = isoDate.split('-');

  const months = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];

  return `${dd}${months[Number(mm) - 1]} ${yyyy}`;
}

export function toIsoDateYYYYMMDD(input?: string | number | Date | null): string {
  if (!input) return '';

  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }

  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  const year = d.getUTCFullYear();
  const month = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}
export function toIsoDateYYYYMMDDDoc(input?: string | null): string {
  if (!input) return '';

  const regex = /^(\d{4}-\d{2}-\d{2})/;
  const result = regex.exec(input);

  return result ? result[1] : '';
}
export function toIsoDateYYYYMMDDAnomaly(input?: string | number | Date | null): string {
  if (!input) return '';

  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input;
  }

  if (typeof input === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
    if (m) {
      return `${m[1]}-${m[2]}-${m[3]}`;
    }
  }

  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function buildDeadloadRequestPath(base: FlightSearchPayload): {
  urlPath: string;
  query: {
    commodityCode: string;
    origin: string;
    depDateTime: string;
    fmLoadIndicator: boolean;
  };
} {
  const mapped = buildPayload('viewInfo', base) as FlightSearchPayload & ViewInfoExtra;

  const req = mapped.ViewFlightSummaryRequest;
  const carrierCode = req?.DatedFlightLeg?.OperatingCarrierCode ?? '';
  const flightNumber = req?.DatedFlightLeg?.OperationalFlightNumber ?? '';

  const commodityCodeCSV = Array.isArray(req?.CommodityCode)
    ? req!.CommodityCode.join(',')
    : req?.CommodityCode ?? '';

  return {
    urlPath: `/cfs/RetrieveFlightSummaryforDeadload/${encodeURIComponent(
      carrierCode
    )}/${encodeURIComponent(flightNumber)}`,
    query: {
      commodityCode: commodityCodeCSV,
      origin: req?.DatedFlightLeg?.OriginStation ?? '',
      depDateTime: normalizeDepDateTime(base.flightDate),

      // depDateTime: req?.DatedFlightLeg?.ScheduledDepartureDateLocal ?? '',
      fmLoadIndicator: true,
    },
  };
}

export function buildDeadloadPayload(base: FlightSearchPayload, codes: string[]) {
  return {
    ...base,
    ViewFlightSummaryRequest: {
      CommodityCode: codes.length ? codes : undefined,
      DatedFlightLeg: {
        OperationalFlightNumber: base.flightNumbers?.[1] ?? '',
        OperatingCarrierCode: base.flightNumbers?.[0] ?? '',
        OriginStation: base.departureStation,
        ScheduledDepartureDateLocal: formatDate(base.flightDate),
      },
      FMLoadIndicator: true,
    },
  };
}

export type ForecastItem = {
  LoadType: 'BLK' | 'ULD';
  OffpointStation?: string;
  UnitType?: string;
  SerialNumber?: string;
  OwnerCode?: string;
  GrossWeight?: number;
  TareWeight?: number;
  Deadload?: {
    CommodityCode?: string;
    NetWeight?: number;
    EstimatedIndicator?: 'Y' | 'N';
  };
  Com?: string;
};

export type ForecastRow = {
  Ind: string | null;
  Type: string | null;
  Dest: string | null;
  ULDorBLK: string | null;
  Weight: number | null;
  ArrFlt: string | null;
  Origin: string | null;
  STA: string | null;
  Allot: string | null;
  Com: string | null;
  UnitComments: string | null;
};

export const DISPLAYED_COLUMNS: string[] = [
  'Type',
  'Dest',
  'ULDorBLK',
  'Weight',
  'ArrFlt',

  'Origin',
  'STA',
  'Allot',
  'Com',
  'UnitComments',
];

export function mapForecastToRows(
  items: LoadItem[],
  isCargoReleased = false
) {
  return (items ?? []).map((item) => {
    const baseType = item?.loadType ?? null;

    const type =
      item?.intactBuildIndicator && item.intactBuildIndicator.trim() !== ''
        ? item.intactBuildIndicator
        : null;
        
        let uldOrBlk: string | null = null;

        if (baseType === 'BLK') {
          uldOrBlk = 'BLK';
        } else if (baseType === 'ULD') {
          const unit = item?.unitType ?? '';
          const serial = item?.serialNumber ?? '';
          const owner = item?.ownerCode ?? '';
        
          if (isCargoReleased) {
            uldOrBlk = `${unit}${serial}${owner}` || unit || null;
          } else if (item?.dummyLoadItem?.toUpperCase() === 'ACTUAL') {
            uldOrBlk = `${unit}${serial}${owner}` || unit || null;
          } else {
            uldOrBlk = unit || null;
          }
        }
    // let uldOrBlk: string | null = null;
    // if (baseType === 'BLK') {
    //   uldOrBlk = 'BLK';
    // } else if (baseType === 'ULD') {
    //   const unit = item?.unitType ?? '';
    //   const serial = item?.serialNumber ?? '';
    //   const owner = item?.ownerCode ?? '';

    //   if (item?.dummyLoadItem?.toUpperCase() === 'ACTUAL') {
    //     uldOrBlk = `${unit}${serial}${owner}` || unit || null;
    //   } else {
    //     uldOrBlk = unit || null;
    //   }
    // }
    uldOrBlk = (item.ba80DeleteIndicator === true && !item.dummyLoadItem) ? `${item.unitType}${item.serialNumber}${item.ownerCode}` : uldOrBlk;
    const weight = item?.grossWeight ?? item?.deadLoad?.netWeight ?? null;
    const allot = item?.customerAllotmentCode ?? null;
    const com = item?.deadLoad?.commodityCode ?? null;
    const unitComments = item?.cargoAgentComment ?? null;
    const origin = item?.arrivalFlightOrigin ?? null;
    const arrflt =
      item?.arrivalCarrierCode && item?.arrivalFlightNumber
        ? `${item.arrivalCarrierCode}${item.arrivalFlightNumber}`
        : '';
    const arrivalCarrierCode = item?.arrivalCarrierCode ?? '';
    const arrivalFlightNumber = item?.arrivalFlightNumber ?? '';
    const staraw = item?.arrivalFlightSTA ?? null;

    return {
      Ind: '',
      Type: type,
      Dest: item?.offpointStation ?? null,
      ULDorBLK: uldOrBlk,
      Weight: weight,
      ArrFlt: arrflt,
      Origin: origin,
      ArrivalCarrierCode: arrivalCarrierCode,
      ArrivalFlightNumber: arrivalFlightNumber,
      STA: formatDateToDDMMMYYYY(staraw),
      Allot: allot,
      Com: com,
      UnitComments: unitComments,
      IntactBuildIndicator: item?.intactBuildIndicator ?? null,
    };
  });
}

export function formatDate(date: string | undefined): string {
  if (!date) return '';
  const [day, month, year] = date.split('-');
  const months: Record<string, string> = {
    JAN: '01',
    FEB: '02',
    MAR: '03',
    APR: '04',
    MAY: '05',
    JUN: '06',
    JUL: '07',
    AUG: '08',
    SEP: '09',
    OCT: '10',
    NOV: '11',
    DEC: '12',
  };
  return `${year}-${months[month]}-${day}T00:00:00`;
}

export function flightKey(payload: FlightSearchPayload): string {
  const carrier = payload?.flightNumbers?.[0] ?? '';
  const number = payload?.flightNumbers?.[1] ?? '';
  const date = (payload?.flightDate || '').substring(0, 10);
  return `${carrier}-${number}-${date}`;
}
export interface FlightDetails {
  carrierCode: string;
  flightNumber: string;
  suffix: string;
}

export function extractFlightDetails(flightNumbers: string[]): {
  carrierCode: string;
  flightNumber: string;
  suffix: string;
} {
  let carrierCode = '';
  let flightNumber = '';
  let suffix = '';

  if (flightNumbers?.length === 2) {
    carrierCode = flightNumbers[0];

    const regex = /^(\d+)([A-Za-z]?)$/;
    const match = regex.exec(flightNumbers[1] || '');

    flightNumber = match?.[1] ?? flightNumbers[1] ?? '';
    suffix = match?.[2] ?? '';
  } else if (flightNumbers?.length === 1) {
    const flight = flightNumbers[0] || '';

    carrierCode = flight.slice(0, 2);

    const FLIGHT_NUMBER_REGEX = /^(\d+)([A-Za-z]?)$/;

    const numPart = flight.slice(2);
    const match = FLIGHT_NUMBER_REGEX.exec(numPart);

    flightNumber = match?.[1] ?? numPart;
    suffix = match?.[2] ?? '';
  }

  return { carrierCode, flightNumber, suffix };
}
export function formatFlightDate(date: string): string {
  if (!date) return '';

  if (date.includes('T')) {
    date = date.split('T')[0];
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  const parts = date.split('-');

  if (parts.length !== 3) {
    return date;
  }

  let [day, month, year] = parts;

  const monthMap: Record<string, string> = {
    JAN: '01',
    FEB: '02',
    MAR: '03',
    APR: '04',
    MAY: '05',
    JUN: '06',
    JUL: '07',
    AUG: '08',
    SEP: '09',
    OCT: '10',
    NOV: '11',
    DEC: '12',
  };

  if (Number.isNaN(Number(month))) {
    const mapped = monthMap[month.toUpperCase()];
    if (!mapped) {
      return date;
    }
    month = mapped;
  } else {
    const num = Number(month);
    if (num < 1 || num > 12) {
      return date;
    }
    month = num.toString().padStart(2, '0');
  }

  const paddedDay = day.padStart(2, '0');

  return `${year}-${month}-${paddedDay}`;
}
export function getFlightParts(payload: any) {
  const extracted = extractFlightDetails(payload?.flightNumbers ?? []);

  return {
    carrierCode: extracted.carrierCode,
    flightNumber: extracted.flightNumber,
    suffix: payload?.flightNumberSuffix ?? extracted.suffix,
  };
}
